import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import {
  type CanvasPopoverEntry,
  type CanvasPopoverSlice,
  createCanvasPopoverSlice,
} from '~/components/v2/store/features/onboarding/slices/createCanvasPopoverSlice';
import {
  createShowFirstUploadPromptSlice,
  type ShowFirstUploadPromptSlice,
} from '~/components/v2/store/features/onboarding/slices/createShowFirstUploadPromptSlice';
import { ZustandStorageKeys } from '~/constants/zustandStorageKeys';

export type OnboardingPreferencesState = ShowFirstUploadPromptSlice & CanvasPopoverSlice;

const VERSION_SHOW_FIRST_UPLOAD_PROMPT_RECORD = 2;

export const useOnboardingPreferencesStore = create<OnboardingPreferencesState>()(
  persist(
    (...args) => ({
      ...createShowFirstUploadPromptSlice(...args),
      ...createCanvasPopoverSlice(...args),
    }),
    {
      name: ZustandStorageKeys.OnboardingPreferences,
      partialize: (state) => ({
        showFirstUploadPrompt: state.showFirstUploadPrompt,
        canvasPopover: state.canvasPopover,
      }),
      storage: createJSONStorage(() => localStorage),
      version: 2,
      migrate: (persistedState: unknown, version: number) => {
        if (version < VERSION_SHOW_FIRST_UPLOAD_PROMPT_RECORD) {
          if (typeof persistedState !== 'object' || persistedState === null) {
            return persistedState;
          }
          const s = Object.fromEntries(Object.entries(persistedState));

          if (
            !('showFirstUploadPrompt' in persistedState) ||
            typeof s.showFirstUploadPrompt !== 'object' ||
            s.showFirstUploadPrompt === null
          ) {
            return { ...s, showFirstUploadPrompt: {} };
          }
          const typed = Object.fromEntries(Object.entries(s.showFirstUploadPrompt));

          return {
            ...s,
            showFirstUploadPrompt:
              typeof typed.workspaceId === 'string' ? { [typed.workspaceId]: typed.value === true } : {},
          };
        }

        return persistedState;
      },
    },
  ),
);

export const useShowFirstUploadPromptValue = (workspaceId?: string): boolean =>
  useOnboardingPreferencesStore((state) => (workspaceId ? (state.showFirstUploadPrompt[workspaceId] ?? false) : false));

const canvasPopoverEntrySelector = (
  state: OnboardingPreferencesState,
  workspaceId: string | undefined,
): CanvasPopoverEntry | undefined => (workspaceId ? state.canvasPopover[workspaceId] : undefined);

export const useCanvasPopoverEntry = (workspaceId: string | undefined): CanvasPopoverEntry | undefined =>
  useOnboardingPreferencesStore((state) => canvasPopoverEntrySelector(state, workspaceId));

export const useIsUserFromOnboardingPrompt = (workspaceId: string | undefined): boolean =>
  useOnboardingPreferencesStore((state) => {
    if (!workspaceId) {
      return false;
    }

    // undefined means the user signed up before this onboarding flow existed
    const showFirstUploadPrompt = state.showFirstUploadPrompt[workspaceId];

    if (showFirstUploadPrompt === undefined) {
      return false;
    }

    return true;
  });
